import { z } from 'zod'

import type { NavItem } from '@/shared/registry'

import { readClassesPermission } from '../class-form/class'
import { completionStatuses } from '../roster/roster'

/** Reading a class's files (CLS-17), which Manage Files opens. */
export const readClassFilesPermission = 'classes.class-files.read'

/** Where the instructor dashboard lives. */
export const instructorDashboardPath = '/classes/instructor-dashboard'

/**
 * Its nav item, for whoever holds the Instructor role, teaching a class now or not (112024), with
 * the classes read its feed needs. The home page sends the same people to the dashboard.
 */
export const instructorDashboardNav: NavItem = {
  id: 'instructor-dashboard',
  labelId: 'classes.dashboard.title',
  path: instructorDashboardPath,
  permission: readClassesPermission,
  isInstructorOnly: true,
}

/** The API's one feed for the dashboard, in the viewer's time zone, with the month shown (`yyyy-MM`) or the current one. */
export function instructorDashboardApi(timeZone: string, month: string | null): string {
  const query = new URLSearchParams({ timeZone })
  if (month !== null) {
    query.set('month', month)
  }
  return `/classes/v1/instructor-dashboard?${query.toString()}`
}

const studentSchema = z.object({
  enrollmentId: z.string(),
  completionStatus: z.enum(completionStatuses),
})

/** An Enrolled student on one of today's sessions. */
export type DashboardStudent = z.infer<typeof studentSchema>

const sessionSchema = z.object({
  classId: z.string(),
  sessionId: z.string(),
  className: z.string(),
  startsOn: z.string(),
  endsOn: z.string(),
  maximumStudents: z.number().int(),
  /** `null` when the viewer may not read the class's roster. */
  enrolled: z.number().int().nullable(),
  /** Today's sessions only, and `null` when the viewer may not read the class's roster. */
  students: z.array(studentSchema).nullable(),
})

/** A session the viewer teaches. */
export type DashboardSession = z.infer<typeof sessionSchema>

const dashboardSchema = z.object({
  now: z.string(),
  timeZone: z.string(),
  today: z.string(),
  weekStart: z.string(),
  month: z.string(),
  todaysSessions: z.array(sessionSchema),
  sessions: z.array(sessionSchema),
})

/** The dashboard's feed: today's sessions, and the week's and the month's. */
export type InstructorDashboardFeed = z.infer<typeof dashboardSchema>

export function parseInstructorDashboard(body: unknown): InstructorDashboardFeed {
  return dashboardSchema.parse(body)
}

/** How a session's Enrolled students stand, as legacy's chart counts them. */
export interface CompletionBreakdown {
  readonly notStarted: number
  readonly inProgress: number
  readonly complete: number
  /** Students Launch gave no progress for, whose completion status does not settle it either. */
  readonly unknown: number
  /** The share that is complete, rounded down, as legacy's "% complete". */
  readonly percentComplete: number
}

/**
 * Counts the students as legacy's dashboard does: complete at 100% progress or a Complete status;
 * not started at 0% (or no progress known) with neither Complete nor Attending; in progress
 * otherwise. `progress` is each enrollment's percent complete, from Launch's slot; a student it
 * leaves out is unknown, unless a Complete or Attending status settles it. With no progress at all,
 * the completion status alone decides.
 */
export function completionBreakdown(
  students: readonly DashboardStudent[],
  progress: ReadonlyMap<string, number> | undefined,
): CompletionBreakdown {
  let notStarted = 0
  let complete = 0
  let unknown = 0
  for (const student of students) {
    const known = progress === undefined ? 0 : progress.get(student.enrollmentId)
    const percent = known ?? 0
    if (percent >= 100 || student.completionStatus === 'complete') {
      complete += 1
    } else if (student.completionStatus === 'attending') {
      continue
    } else if (known === undefined) {
      unknown += 1
    } else if (percent <= 0) {
      notStarted += 1
    }
  }
  const total = students.length
  return {
    notStarted,
    inProgress: total - notStarted - complete - unknown,
    complete,
    unknown,
    percentComplete: total === 0 ? 0 : Math.floor((complete * 100) / total),
  }
}
